<template>
  <v-flex xs12>
    <data-source-table
      itemController="advertisement"
      itemIdentifyField="name"
      :headers="headers"
      :options.sync="options"
      :selected-items.sync="selectedItems"
      :get-data-hook="getDataHook"
      :add-item-hook="addItemHook"
      ref="dataSourceRef"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <confirm-dialog
            :is-open.sync="dialogs.editdialog.isOpened"
            dialog-max-width="75%"
            @confirm="saveNew"
            confirm-button-text="save"
          >
            <template v-slot:activator>
              <v-btn
                class="mx-2" color="primary"
                dark fab small
                @click="openDialog('editdialog')"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <slot>
                      <v-icon v-bind="attrs" v-on="on" dark>
                        mdi-plus
                      </v-icon>
                    </slot>
                  </template>
                  create new Advertisement
                </v-tooltip>
              </v-btn>
            </template>
            <template v-slot:title>
              <span class="headline">Advertisement</span>
            </template>
            <template v-slot:body>
              <v-container>
                <v-row>
                  <v-col cols="3" sm="3" md="3">
                    <v-text-field
                      v-model="dialogs.editdialog.data.name"
                      label="Name"
                      :rules="[rules.required]"
                    />
                  </v-col>
                  <v-col cols="2" sm="2" md="2">
                    <v-text-field
                      v-model="dialogs.editdialog.data.position"
                      label="Position"
                      type="number"
                      :rules="[rules.positionOrRecurrence(dialogs.editdialog.data)]"
                    />
                  </v-col>
                  <v-col cols="1" sm="1" md="1" class="d-flex justify-center">
                    <div class="align-self-center">OR</div>
                  </v-col>
                  <v-col cols="2" sm="2" md="2">
                    <v-text-field
                      v-model="dialogs.editdialog.data.recurrence"
                      label="Recurrence"
                      type="number"
                      :rules="[rules.positionOrRecurrence(dialogs.editdialog.data)]"
                    />
                  </v-col>
                  <v-col cols="4" sm="4" md="4">
                    <v-text-field
                      v-model="dialogs.editdialog.data.frequency"
                      label="Frequency"
                      type="number"
                      :rules="[rules.required]"
                    />
                  </v-col>
                  <v-col cols="4" sm="4" md="4">
                    <GeotagList
                      :geotag-model.sync="dialogs.editdialog.data.geotags"
                      multiple
                    />
                  </v-col>
                  <v-col cols="4" sm="4" md="4">
                    <DataList
                      :item-model.sync="dialogs.editdialog.data.customers"
                      :type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
                      label="customers"
                      multiple
                      clearable
                      chips
                      small-chips
                      deletable-chips
                    />
                  </v-col>
                  <v-col cols="4" sm="4" md="4">
                    <v-file-input
                      v-model="dialogs.editdialog.data.media"
                      accept=".mp4,image/*"
                      label="Click here to upload .mp4 or image file"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </template>
          </confirm-dialog>

          <DialogVideoPlayer
            :show.sync="videoDialog.show"
            :src.sync="videoDialog.src"
          />

          <v-spacer/>

          <v-btn
              class="mx-2" color="blue darken-1"
              outlined small
              @click="$refs.dataSourceRef ? $refs.dataSourceRef.openDialogSelectColumns() : false"
          >
            Select columns
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:filter_geotags>
        <GeotagList
          :geotag-model.sync="options.filters.geotags"
          label="geotags"
          dense
          multiple
        />
      </template>
      <template v-slot:filter_customers>
        <DataList
          :item-model.sync="options.filters.customers"
          :type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
          label="customers"
          multiple
          clearable
          dense
          chips
          small-chips
          deletable-chips
        />
      </template>

      <template v-slot:item.geotags="{ item }">
        <EditableList
          label="geotag"
          :object="item"
          :values="item.geotags"
          :list-type-object="$constants.DATA_LISTS.GEOTAG_LIST"
          description-field="country_state"
          @addItem="addGeotag"
          @removeItem="removeGeotag"
        />
      </template>

      <template v-slot:item.customers="{ item }">
        <EditableList
          label="customer"
          :object="item"
          :values="item.customers"
          :list-type-object="$constants.DATA_LISTS.CUSTOMER_LIST"
          @addItem="addCustomer"
          @removeItem="removeCustomer"
        />
      </template>

      <template v-slot:item.videos="{ item }">
        <div>
          <div v-for="video in item.videos" :key="video.id">
            <v-img
              contain
              :src="video.preview_url"
              weight="200"
              height="200"
              aspect-ratio="1"
            >
              <div class="d-flex align-center justify-center fill-height">
                <v-btn
                  v-if="video.status === 'done'"
                  x-small fab
                  @click="openPlayVideoDialog(video.uri)"
                >
                  <v-icon>mdi-play</v-icon>
                </v-btn>
                <div v-else>Status: {{ video.status }}</div>
              </div>
            </v-img>
          </div>
        </div>
      </template>

    </data-source-table>
  </v-flex>
</template>

<script>
import { mapActions } from 'vuex';
import EditableList from '../components/EditableList.vue';
import DialogVideoPlayer from '../components/dialogs/DialogVideoPlayer.vue';
import DataSourceTable from '../components/DataSource/DataTable.vue';
import ConfirmDialog from '../components/dialogs/ConfirmDialog.vue';
import manageDialogsMixin from '../mixins/manageDialogsMixin';
import GeotagList from '../components/DataLists/GeotagList.vue';
import DataList from '../components/DataLists/DataList.vue';

export default {
  name: 'Advertisements',
  components: {
    DataList,
    GeotagList,
    ConfirmDialog,
    DataSourceTable,
    EditableList,
    DialogVideoPlayer,
  },
  data: () => ({
    itemClone: {},
    selectedItems: [],
    options: { filters: { } },
    rules: {
      required: (v) => (!!v) || 'Field is required',
      positionOrRecurrence: (newAd) => (!!newAd.position && !newAd.recurrence)
        || (!newAd.position && !!newAd.recurrence)
        || 'Position Or Recurrence is required',
    },
    videoDialog: {
      show: false,
      src: '',
    },
    dialogs: {
      editdialog: {
        isOpened: false,
        data: {
          name: '',
          position: null,
          recurrence: null,
          frequency: null,
          geotags: null,
          customers: null,
          media: null,
        },
        defaults: {
          name: '',
          position: null,
          recurrence: null,
          frequency: null,
          geotags: null,
          customers: null,
          media: null,
        },
      },
    },
  }),

  mixins: [
    manageDialogsMixin,
  ],

  created() {
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    getDataHook() {
    },
    addItemHook() {
    },

    saveNew() {
      const data = new FormData();
      Object.keys(this.dialogs.editdialog.data).forEach((key) => {
        if (this.dialogs.editdialog.data[key]) {
          data.append(key, this.dialogs.editdialog.data[key]);
        }
      });

      this.$requestHelpers
        .post('api/admin/advertisement',
          data,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        .then((response) => {
          if (response.data.success) {
            this.$refs.dataSourceRef.addItem(response.data.result);
          }
        }).finally(() => {
          this.closeDialog('editdialog');
        });
    },

    updateAd(field, value, item) {
      return this.$refs.dataSourceRef.updateItemField(field, value, item);
    },

    addGeotag(item, geotag) {
      if (!geotag) return;

      this.updateAd('add_geotag', geotag, item);
    },

    removeGeotag(item, geotag) {
      if (!geotag) return;

      this.updateAd('remove_geotag', geotag, item);
    },

    addCustomer(item, customer) {
      if (!customer) return;

      this.updateAd('add_customer', customer, item);
    },

    removeCustomer(item, customer) {
      if (!customer) return;

      this.updateAd('remove_customer', customer, item);
    },

    openPlayVideoDialog(videoSrc) {
      Object.assign(this.videoDialog, { show: true, src: videoSrc });
    },
  },

  computed: {
    headers() {
      return [
        {
          text: 'id',
          value: 'id',
          width: '120px',
        },
        {
          text: 'name',
          value: 'name',
          edit: { type: 'text', emptyPlaceholder: true, updateAllFields: true },
        },
        {
          text: 'position',
          value: 'position',
          edit: { type: 'number', emptyPlaceholder: true },
        },
        {
          text: 'recurrence',
          value: 'recurrence',
          edit: { type: 'number', emptyPlaceholder: true },
        },
        {
          text: 'frequency',
          value: 'frequency',
          edit: { type: 'number', emptyPlaceholder: true },
        },
        {
          text: 'geotags',
          value: 'geotags',
          sortable: false,
        },
        {
          text: 'customers',
          value: 'customers',
          sortable: false,
        },
        {
          text: 'videos',
          value: 'videos',
          width: '200px',
          sortable: false,
          filter: { type: 'disable' },
        },
        {
          text: 'enabled',
          value: 'enabled',
          width: '120px',
          filter: { type: 'select', items: [{ id: 1, name: 'on' }, { id: 0, name: 'off' }] },
          edit: { type: 'switch' },
        },
        {
          text: 'count in analytics',
          value: 'is_count_in_analytics',
          width: '120px',
          filter: { type: 'select', items: [{ id: 1, name: 'on' }, { id: 0, name: 'off' }] },
          edit: { type: 'switch' },
        },
        {
          text: '',
          value: 'deleteItem',
          sortable: false,
          width: '80px',
        },
      ];
    },
  },
};
</script>
